/**
 * Reset Password Page
 * Honest local-flow fallback: request a reset email only
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { LoadingSpinner } from '@/components/common/loading-spinner';
import { usePasswordReset } from '@/hooks/use-auth';
import { toast } from '@/stores/ui-store';
import { ArrowLeft, Mail } from 'lucide-react';

export default function ResetPasswordPage() {
  const navigate = useNavigate();
  const passwordResetMutation = usePasswordReset();

  const [email, setEmail] = useState('');
  const [resetSent, setResetSent] = useState(false);

  const handlePasswordReset = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email) {
      toast.error('Please enter your email address');
      return;
    }

    try {
      await passwordResetMutation.mutateAsync(email);
      setResetSent(true);
      toast.success('Reset link sent', 'Check your email for the password reset link');
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to send reset link';
      toast.error('Reset failed', message);
    }
  };

  return (
    <div className="min-h-screen bg-[#ffffff] flex items-center justify-center p-4">
      <Card className="w-full max-w-md border-[#e5e5e5]">
        <CardHeader className="space-y-1">
          <div className="flex items-center gap-2 mb-2">
            <Button
              variant="ghost"
              size="sm"
              className="h-8 px-2 -ml-2"
              onClick={() => navigate('/login')}
            >
              <ArrowLeft className="h-4 w-4 mr-1" />
              Back
            </Button>
          </div>
          <CardTitle className="text-2xl font-bold text-[#000000]">
            Reset Password
          </CardTitle>
          <CardDescription className="text-[#666666]">
            {resetSent
              ? 'Check your email for the reset link'
              : 'Request a password reset email. Local reset completion is handled via email, not on this page.'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {resetSent ? (
            <div className="text-center py-6">
              <div className="w-16 h-16 rounded-full bg-[#000000] flex items-center justify-center mx-auto mb-4">
                <Mail className="h-8 w-8 text-[#ffffff]" />
              </div>
              <p className="text-[#333333] mb-4">
                We've sent a password reset link to:
              </p>
              <p className="font-medium text-[#000000] break-all">{email}</p>
              <Button
                variant="outline"
                className="mt-6 w-full"
                onClick={() => navigate('/login')}
              >
                Back to Login
              </Button>
            </div>
          ) : (
            <form onSubmit={handlePasswordReset} className="space-y-4">
              <p className="text-sm text-[#666666]">
                If you opened an older recovery link and it did not complete the reset, request a fresh email below.
              </p>

              <div className="space-y-2">
                <Label htmlFor="reset-email" className="text-[#333333]">
                  Email
                </Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#666666]" />
                  <Input
                    id="reset-email"
                    type="email"
                    placeholder="name@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-10"
                    required
                  />
                </div>
              </div>

              <Button
                type="submit"
                className="w-full bg-[#000000] hover:bg-[#1a1a1a]"
                disabled={passwordResetMutation.isPending}
              >
                {passwordResetMutation.isPending ? (
                  <LoadingSpinner size="sm" />
                ) : (
                  'Send Reset Link'
                )}
              </Button>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
